import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { format } from 'date-fns';
import { PaperAirplaneIcon } from '@heroicons/react/24/solid';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../AuthContext';

const API_BASE = (process.env.REACT_APP_API_BASE || 'https://api.insightos.co.uk').replace(/\/$/, '');
const SALES_CAPTURE_ENDPOINT = `${API_BASE}/lead/salescapture`;
const SALES_LEADS_ENDPOINT = `${API_BASE}/sales/leadtable`;
const SALES_TEMPLATES_ENDPOINT = `${API_BASE}/sales/gettemplates`;

const normalizeTemplateRows = (payload) => {
	if (!payload) return [];
	const raw = payload?.data ?? payload?.templates ?? payload;
	if (Array.isArray(raw)) {
		if (raw.length === 1 && Array.isArray(raw[0])) {
			return raw[0];
		}
		return raw;
	}
	if (Array.isArray(raw?.rows)) {
		return raw.rows;
	}
	return [];
};

const toDateInputValue = (value) => {
	if (!value) return '';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return String(value).slice(0, 10);
	}
	return format(date, 'yyyy-MM-dd');
};

const formatDisplayDate = (value) => {
	if (!value) return '—';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return value;
	}
	return format(date, 'dd MMM yyyy');
};

const getLeadLabel = (lead) =>
	lead?.LeadName || lead?.Company || lead?.CompanyName || lead?.BusinessName || `Lead #${lead?.id ?? ''}`;

const normalizeLeadIdValue = (value) => {
	if (value === null || value === undefined) return null;
	const numeric = Number(value);
	if (!Number.isNaN(numeric)) {
		return numeric;
	}
	const stringValue = typeof value === 'string' ? value.trim() : String(value);
	return stringValue.length ? stringValue : null;
};

const ensureLeadId = (lead) => {
	if (!lead || typeof lead !== 'object') {
		return lead;
	}
	const currentId = lead.id;
	const fallbackId =
		currentId ??
		lead.ID ??
		lead.Id ??
		lead.leadId ??
		lead.LeadId ??
		lead.LeadID ??
		null;
	const normalizedId = normalizeLeadIdValue(fallbackId);
	if (normalizedId === null || normalizedId === undefined || normalizedId === currentId) {
		return lead;
	}
	return { ...lead, id: normalizedId };
};

const collectLeadIds = (leadList) =>
	Array.isArray(leadList)
		? leadList
				.map((item) => normalizeLeadIdValue(item?.id ?? item?.ID ?? item?.Id ?? item?.LeadId ?? item?.LeadID ?? item?.leadId))
				.filter((value) => value !== null && value !== undefined)
		: [];

const safeText = (value, fallback = '—') => {
	if (value === null || value === undefined || value === '') {
		return fallback;
	}
	return value;
};

const summarizeText = (value, maxLength = 60) => {
	const normalized = String(value ?? '').replace(/\s+/g, ' ').trim();
	if (!normalized) {
		return '';
	}
	return normalized.length > maxLength ? `${normalized.slice(0, maxLength)}…` : normalized;
};

const describeTemplateOption = (template) => {
	if (!template) {
		return 'Template';
	}
	const snippet = summarizeText(template.content);
	const base = `${template.type || 'General'} · ${template.name || 'Template'}`;
	return snippet ? `${base} — ${snippet}` : base;
};

const pickField = (source, ...candidates) => {
	if (!source) return null;
	for (const key of candidates) {
		if (source[key] !== undefined && source[key] !== null) {
			return source[key];
		}
	}
	return null;
};

const formatDateTime = (value) => {
	if (!value) return '—';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return String(value);
	}
	return format(date, 'dd MMM yyyy HH:mm');
};

const UNSUBSCRIBE_DISCLAIMER = 'If you wish to unsubscribe from future emails just reply "Unsubscribe".';
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

const normalizeOfficerEmails = (raw) =>
	String(raw ?? '')
		.split(/[;,]/)
		.map((entry) => entry.trim())
		.filter((entry) => entry.length > 0 && EMAIL_REGEX.test(entry));

const ensureInitialEmailDisclaimer = (body) => {
	const text = String(body ?? '').trimEnd();
	if (!text) {
		return '';
	}
	const normalized = text.toLowerCase();
	if (normalized.includes('unsubscribe from future emails') && normalized.includes('unsubscribe"')) {
		return text;
	}
	const spacer = text.endsWith('\n') ? '\n' : '\n\n';
	return `${text}${spacer}${UNSUBSCRIBE_DISCLAIMER}`;
};

const PROGRESS_OPTIONS = [
	{ value: 'New', label: 'New', textClass: 'text-blue-200', barClass: 'bg-blue-400' },
	{ value: 'Contacted', label: 'Contacted', textClass: 'text-purple-200', barClass: 'bg-purple-400' },
	{ value: 'No Reply', label: 'No Reply', textClass: 'text-yellow-200', barClass: 'bg-yellow-300' },
	{ value: 'Replied', label: 'Replied', textClass: 'text-indigo-200', barClass: 'bg-indigo-300' },
	{ value: 'Interested', label: 'Interested', textClass: 'text-emerald-200', barClass: 'bg-emerald-300' },
	{ value: 'Demo Booked', label: 'Demo Booked', textClass: 'text-emerald-200', barClass: 'bg-emerald-400' },
	{ value: 'Demo Completed', label: 'Demo Completed', textClass: 'text-green-200', barClass: 'bg-green-500' },
	{ value: 'Trial Started', label: 'Trial Started', textClass: 'text-cyan-200', barClass: 'bg-cyan-400' },
	{ value: 'Converted', label: 'Converted', textClass: 'text-teal-200', barClass: 'bg-teal-400' },
	{ value: 'Lost', label: 'Lost', textClass: 'text-rose-200', barClass: 'bg-rose-500' },
];

const PROGRESS_VALUE_SET = new Set(PROGRESS_OPTIONS.map((option) => option.value));

const getProgressMeta = (value) =>
	PROGRESS_OPTIONS.find((option) => option.value === value) || {
		value: value || 'Unspecified',
		label: value || 'Unspecified',
		textClass: 'text-slate-300',
		barClass: 'bg-slate-500',
	};

export default function SalesDashboard() {
	const auth = useContext(AuthContext);
	const staffId = auth.id;
	const staffName = auth.name;
	const navigate = useNavigate();
	const location = useLocation();

	const [leads, setLeads] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [successMessage, setSuccessMessage] = useState('');
	const [activeTab, setActiveTab] = useState('available');
	const [availableSelection, setAvailableSelection] = useState([]);
	const [ownedSelection, setOwnedSelection] = useState([]);
	const [assigning, setAssigning] = useState(false);
	const [notesDrafts, setNotesDrafts] = useState({});
	const [notesSavingId, setNotesSavingId] = useState(null);
	const [templates, setTemplates] = useState([]);
	const [templatesLoading, setTemplatesLoading] = useState(false);
	const [templateError, setTemplateError] = useState('');
	const [templateAutoAttempted, setTemplateAutoAttempted] = useState(false);
	const [selectedTemplateId, setSelectedTemplateId] = useState('');
	const [bulkCampaignRunning, setBulkCampaignRunning] = useState(false);
	const [bulkCampaignFeedback, setBulkCampaignFeedback] = useState('');
	const [bulkCampaignError, setBulkCampaignError] = useState('');
	const [draftPanelOpen, setDraftPanelOpen] = useState(false);
	const [initialEmailDrafts, setInitialEmailDrafts] = useState({});
	const [draftMessages, setDraftMessages] = useState({});
	const [initialEmailRecipients, setInitialEmailRecipients] = useState({});
	const [bulkSendRunning, setBulkSendRunning] = useState(false);
	const [bulkSendFeedback, setBulkSendFeedback] = useState('');
	const [bulkSendError, setBulkSendError] = useState('');
	const [progressFilter, setProgressFilter] = useState('all');
	const [progressUpdating, setProgressUpdating] = useState({});
	const [adminDetails, setAdminDetails] = useState(null);
	const [adminLoading, setAdminLoading] = useState(false);
	const [adminError, setAdminError] = useState('');
	const [smtpCheckLoading, setSmtpCheckLoading] = useState(false);
	const [smtpCheckMessage, setSmtpCheckMessage] = useState('');
	const [expandedLeadIds, setExpandedLeadIds] = useState([]);
	const [meetingLead, setMeetingLead] = useState(null);
	const [meetingInvitee, setMeetingInvitee] = useState('');
	const [meetingModalOpen, setMeetingModalOpen] = useState(false);
	const [meetingValidationError, setMeetingValidationError] = useState('');

	useEffect(() => {
		if (location.state?.meetingSuccess) {
			setSuccessMessage(location.state.meetingSuccess);
			navigate(location.pathname, { replace: true, state: {} });
		}
	}, [location, navigate]);

	const fetchLeads = useCallback(async () => {
		setLoading(true);
		setError('');
		try {
			const response = await fetch(SALES_LEADS_ENDPOINT, {
				method: 'GET',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
			});

			const payload = await response.json().catch(() => null);
			if (!response.ok) {
				const reason = payload?.message || `Failed to fetch sales leads (HTTP ${response.status})`;
				throw new Error(reason);
			}

			const rows = Array.isArray(payload?.leads)
				? payload.leads
				: Array.isArray(payload?.data)
					? payload.data
					: Array.isArray(payload)
						? payload
						: [];
			setLeads(rows.map((lead) => ensureLeadId(lead)));
		} catch (err) {
			console.error(err);
			setError(err.message || 'Unable to load leads for the sales dashboard.');
		} finally {
			setLoading(false);
		}
	}, []);

	const fetchAdminDetails = useCallback(async () => {
		setAdminLoading(true);
		setAdminError('');
		setSmtpCheckMessage('');
		try {
			const [detailsResponse, settingsResponse] = await Promise.all([
				fetch(`${API_BASE}/admin/getadmindetails`, {
					method: 'GET',
					headers: { 'Content-Type': 'application/json' },
					credentials: 'include',
				}),
				fetch(`${API_BASE}/sales/admin-settings`, {
					method: 'GET',
					headers: { 'Content-Type': 'application/json' },
					credentials: 'include',
				}),
			]);

			const payload = await detailsResponse.json().catch(() => null);
			const settingsPayload = await settingsResponse.json().catch(() => null);

			if (!detailsResponse.ok) {
				const reason = payload?.message || `Failed to fetch admin details (HTTP ${detailsResponse.status})`;
				throw new Error(reason);
			}

			if (!settingsResponse.ok) {
				const reason = settingsPayload?.message || `Failed to fetch admin settings (HTTP ${settingsResponse.status})`;
				throw new Error(reason);
			}

			const detailsCandidate =
				payload?.details ??
				payload?.data ??
				payload?.admin ??
				(Array.isArray(payload) ? payload[0] : payload);

			if (!detailsCandidate || typeof detailsCandidate !== 'object') {
				throw new Error('Admin details response empty.');
			}

			const settingsData = settingsPayload?.data && typeof settingsPayload.data === 'object' ? settingsPayload.data : {};
			setAdminDetails({ ...detailsCandidate, ...settingsData });
		} catch (err) {
			console.error(err);
			setAdminError(err.message || 'Unable to load staff details.');
			setAdminDetails(null);
		} finally {
			setAdminLoading(false);
		}
	}, []);

	const checkSmtpConnection = useCallback(async () => {
		if (!adminDetails) return;
		setSmtpCheckLoading(true);
		setSmtpCheckMessage('');
		try {
			const response = await fetch(`${API_BASE}/sales/smptp/check`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify({
					host: String(adminDetails?.SMTPHost || ''),
					port: String(adminDetails?.SMPTPPort || ''),
					username: String(adminDetails?.SMPTUsername || ''),
					password: String(adminDetails?.SMPTPassword || ''),
					secure: Number(adminDetails?.SMPTSecure || 0) === 1,
				}),
			});

			const payload = await response.json().catch(() => null);
			if (!response.ok || !payload?.success) {
				throw new Error(payload?.message || `SMTP check failed (HTTP ${response.status})`);
			}

			setSmtpCheckMessage(payload?.message || (payload?.connected ? 'SMTP connected' : 'SMTP not connected'));
		} catch (err) {
			console.error(err);
			setSmtpCheckMessage(err.message || 'SMTP check failed');
		} finally {
			setSmtpCheckLoading(false);
		}
	}, [adminDetails]);

	useEffect(() => {
		fetchLeads();
	}, [fetchLeads]);

	useEffect(() => {
		fetchAdminDetails();
	}, [fetchAdminDetails]);

	const availableLeads = useMemo(
		() =>
			leads.filter((lead) => {
				const officerEmails = String(lead.OfficerEmails ?? '').trim();
				const hasEmails = officerEmails.length > 0;
				const companyHit = Number(lead.CompaniesHouseHit ?? 0) === 1;
				const unassigned = !lead.SalesmanId;
				return hasEmails && companyHit && unassigned;
			}),
		[leads],
	);

	const ownedLeads = useMemo(
		() =>
			leads.filter((lead) => {
				if (!staffId) return false;
				return String(lead.SalesmanId ?? '') === String(staffId);
			}),
		[leads, staffId],
	);

	const selectedAvailableLeads = useMemo(
		() => availableLeads.filter((lead) => availableSelection.includes(lead.id)),
		[availableLeads, availableSelection],
	);

	const selectedOwnedLeads = useMemo(
		() => ownedLeads.filter((lead) => ownedSelection.includes(lead.id)),
		[ownedLeads, ownedSelection],
	);

	const filteredOwnedLeads = useMemo(() => {
		if (progressFilter === 'all') {
			return ownedLeads;
		}
		return ownedLeads.filter((lead) => {
			const normalized = PROGRESS_VALUE_SET.has(lead.Progress) ? lead.Progress : 'New';
			return normalized === progressFilter;
		});
	}, [ownedLeads, progressFilter]);

	const progressStats = useMemo(() => {
		const counts = PROGRESS_OPTIONS.reduce((acc, option) => {
			acc[option.value] = 0;
			return acc;
		}, {});
		ownedLeads.forEach((lead) => {
			const key = PROGRESS_VALUE_SET.has(lead.Progress) ? lead.Progress : 'New';
			counts[key] = (counts[key] || 0) + 1;
		});
		return counts;
	}, [ownedLeads]);

	useEffect(() => {
		setAvailableSelection((prev) => prev.filter((id) => availableLeads.some((lead) => lead.id === id)));
	}, [availableLeads]);

	useEffect(() => {
		setOwnedSelection((prev) => prev.filter((id) => ownedLeads.some((lead) => lead.id === id)));
	}, [ownedLeads]);

	const loadTemplates = useCallback(
		async (options = {}) => {
			setTemplatesLoading(true);
			if (!options.keepError) {
				setTemplateError('');
			}
			try {
				const response = await fetch(SALES_TEMPLATES_ENDPOINT, {
					method: 'GET',
					headers: { 'Content-Type': 'application/json' },
					credentials: 'include',
				});

				const payload = await response.json().catch(() => null);
				if (!response.ok) {
					const isRateLimited = response.status === 429;
					const reason = payload?.message ||
						(isRateLimited
							? 'Template service is rate limiting requests. Please pause for a moment before trying again.'
							: 'Failed to fetch templates');
					throw new Error(reason);
				}

				const rows = normalizeTemplateRows(payload)
					.filter((row) => row && (row.id || row.TemplateId))
					.map((row) => ({
						id: row.id ?? row.TemplateId ?? row.ID,
						name: row.Subject || row.Name || `Template ${row.id ?? row.TemplateId}`,
						type: row.Type || 'General',
						sequence: Number(row.Sequence ?? 0),
						content: row.Content || row.content || row.Template || row.template || '',
					}))
					.sort((a, b) => {
						const typeCompare = a.type.localeCompare(b.type);
						if (typeCompare !== 0) return typeCompare;
						if (!Number.isNaN(a.sequence) && !Number.isNaN(b.sequence)) {
							return a.sequence - b.sequence;
						}
						return a.name.localeCompare(b.name);
					});

				setTemplates(rows);
				setTemplateError('');
			} catch (err) {
				console.error(err);
				setTemplateError(err.message || 'Unable to load templates.');
				setTemplates([]);
			} finally {
				setTemplatesLoading(false);
			}
		},
		[],
	);

	useEffect(() => {
		if (activeTab === 'owned' && !templateAutoAttempted) {
			setTemplateAutoAttempted(true);
			loadTemplates();
		}
	}, [activeTab, templateAutoAttempted, loadTemplates]);

	useEffect(() => {
		setInitialEmailDrafts((prev) => {
			const next = { ...prev };
			const keepKeys = new Set(ownedSelection.map((id) => String(id)));
			Object.keys(next).forEach((key) => {
				if (!keepKeys.has(key)) {
					delete next[key];
				}
			});
			ownedSelection.forEach((leadId) => {
				const key = String(leadId);
				if (next[key] === undefined) {
					const lead = ownedLeads.find((item) => item.id === leadId);
					next[key] = lead?.InitialEmail ?? '';
				}
			});
			return next;
		});
	}, [ownedSelection, ownedLeads]);

	useEffect(() => {
		setInitialEmailRecipients((prev) => {
			const next = { ...prev };
			let changed = false;
			const activeIds = new Set(ownedLeads.map((lead) => String(lead.id)));
			Object.keys(next).forEach((key) => {
				if (!activeIds.has(key)) {
					delete next[key];
					changed = true;
				}
			});
			ownedLeads.forEach((lead) => {
				const key = String(lead.id);
				if (!next[key]) {
					const officerList = normalizeOfficerEmails(lead.OfficerEmails);
					const fallback = (lead.Email || '').trim();
					const defaultRecipient = officerList[0] || fallback || '';
					if (defaultRecipient) {
						next[key] = defaultRecipient;
						changed = true;
					}
				}
			});
			return changed ? next : prev;
		});
	}, [ownedLeads]);

	const toggleSelection = (leadId) => {
		if (activeTab === 'available') {
			setAvailableSelection((prev) =>
				prev.includes(leadId) ? prev.filter((id) => id !== leadId) : [...prev, leadId],
			);
		} else {
			setOwnedSelection((prev) =>
				prev.includes(leadId) ? prev.filter((id) => id !== leadId) : [...prev, leadId],
			);
		}
	};

	const toggleSelectAll = () => {
		if (activeTab === 'available') {
			const allIds = availableLeads.map((lead) => lead.id);
			const isFullySelected = availableSelection.length === allIds.length;
			setAvailableSelection(isFullySelected ? [] : allIds);
		} else {
			const allIds = ownedLeads.map((lead) => lead.id);
			const isFullySelected = ownedSelection.length === allIds.length;
			setOwnedSelection(isFullySelected ? [] : allIds);
		}
	};

	const toggleLeadDetails = (leadId) => {
		setExpandedLeadIds((prev) =>
			prev.includes(leadId)
				? prev.filter((id) => id !== leadId)
				: [...prev, leadId],
		);
	};

	const handleTemplateRetry = () => {
		setTemplateAutoAttempted(true);
		loadTemplates();
	};

	const handleClaimSelected = async () => {
		if (!staffId || !staffName) {
			setError('Missing StaffId/Name in your session. Please relog.');
			return;
		}

		if (selectedAvailableLeads.length === 0) {
			setError('Select at least one lead to capture.');
			return;
		}

		const leadIds = collectLeadIds(selectedAvailableLeads);
		if (leadIds.length === 0) {
			setError('Selected leads are missing identifiers. Please refresh and try again.');
			return;
		}

		setAssigning(true);
		setError('');
		setSuccessMessage('');

		try {
			const response = await fetch(SALES_CAPTURE_ENDPOINT, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify({
					action: 'capture',
					leadIds,
				}),
			});

			let payload = null;
			try {
				payload = await response.json();
			} catch (parseError) {
				payload = null;
			}

			if (!response.ok) {
				const reason = payload?.message || `Failed to capture leads (HTTP ${response.status})`;
				throw new Error(reason);
			}

			setSuccessMessage(payload?.message || `Assigned ${leadIds.length} lead(s) to you.`);
			setAvailableSelection([]);
			await fetchLeads();
		} catch (err) {
			console.error(err);
			setError(err.message || 'Unable to capture selected leads.');
		} finally {
			setAssigning(false);
		}
	};

	const handleNotesChange = (leadId, field, value) => {
		const key = String(leadId);
		const sourceLead = leads.find((lead) => lead.id === leadId);
		setNotesDrafts((prev) => {
			const existing =
				prev[key] ?? {
					Conversation: sourceLead?.Conversation ?? '',
					LastContacted: toDateInputValue(sourceLead?.LastContacted),
					NextContact: toDateInputValue(sourceLead?.NextContact),
				};
			return {
				...prev,
				[key]: {
					...existing,
					[field]: value,
				},
			};
		});
	};

	const getNotesDraft = (lead) => {
		const key = String(lead.id);
		const draft = notesDrafts[key];
		return {
			Conversation: draft?.Conversation ?? lead.Conversation ?? '',
			LastContacted: draft?.LastContacted ?? toDateInputValue(lead.LastContacted),
			NextContact: draft?.NextContact ?? toDateInputValue(lead.NextContact),
		};
	};

	const handleSaveNotes = async (leadId) => {
		const key = String(leadId);
		const draft = notesDrafts[key];
		const lead = leads.find((item) => item.id === leadId);
		if (!draft || !lead) {
			setError('Nothing to update for this lead.');
			return;
		}

		setNotesSavingId(leadId);
		setError('');
		setSuccessMessage('');

		try {
			const response = await fetch(SALES_CAPTURE_ENDPOINT, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify({
					action: 'update-details',
					leadIds: [leadId],
					salesmanId: staffId,
					salesmanName: staffName,
					updates: [
						{
							leadId,
							conversation: draft.Conversation ?? '',
							lastContacted: draft.LastContacted || null,
							nextContact: draft.NextContact || null,
						},
					],
				}),
			});

			if (!response.ok) {
				throw new Error('Failed to save notes');
			}

			setSuccessMessage('Lead notes updated.');
			setNotesSavingId(null);
			setNotesDrafts((prev) => {
				const next = { ...prev };
				delete next[key];
				return next;
			});
			await fetchLeads();
		} catch (err) {
			console.error(err);
			setError('Unable to save lead notes.');
			setNotesSavingId(null);
		}
	};

	const handleBulkApplyTemplate = async () => {
		if (!selectedTemplateId) {
			setBulkCampaignError('Select an AI template first.');
			return;
		}

		if (selectedOwnedLeads.length === 0) {
			setBulkCampaignError('Select at least one owned lead to update its Initial Email.');
			return;
		}

		setBulkCampaignRunning(true);
		setBulkCampaignError('');
		setBulkCampaignFeedback('');

		let successCount = 0;
		let failureCount = 0;
		const templateNumeric = Number(selectedTemplateId);
		const templatePayload = Number.isNaN(templateNumeric) ? selectedTemplateId : templateNumeric;

		try {
			for (const lead of selectedOwnedLeads) {
				try {
					const response = await fetch(`${API_BASE}/sales/firstcampaign`, {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						credentials: 'include',
						body: JSON.stringify({ leadId: lead.id, templateId: templatePayload }),
					});

					if (!response.ok) {
						throw new Error('Campaign attach failed');
					}
					successCount += 1;
				} catch (innerErr) {
					console.error(innerErr);
					failureCount += 1;
				}
			}

			setBulkCampaignFeedback(`Initial Emails updated via AI template. Success: ${successCount}, Failed: ${failureCount}.`);
			await fetchLeads();
		} catch (err) {
			console.error(err);
			setBulkCampaignError('Unable to apply the AI template to Initial Emails.');
		} finally {
			setBulkCampaignRunning(false);
		}
	};

	const handleDraftChange = (leadId, value) => {
		const key = String(leadId);
		setInitialEmailDrafts((prev) => ({
			...prev,
			[key]: value,
		}));
	};

	const handleRecipientChange = (leadId, value) => {
		const key = String(leadId);
		setInitialEmailRecipients((prev) => ({
			...prev,
			[key]: value.trim(),
		}));
	};

	const handleBulkSendEmails = async () => {
		if (selectedOwnedLeads.length === 0) {
			setBulkSendError('Select at least one lead whose Initial Email should be sent.');
			return;
		}

		setBulkSendRunning(true);
		setBulkSendFeedback('');
		setBulkSendError('');

		let sent = 0;
		let failed = 0;

		try {
			for (const lead of selectedOwnedLeads) {
				const key = String(lead.id);
				const draft = (initialEmailDrafts[key] ?? lead.InitialEmail ?? '').trim();
				if (!draft) {
					failed += 1;
					setDraftMessages((prev) => ({
						...prev,
						[key]: { success: '', error: 'Initial Email draft missing content.' },
					}));
					continue;
				}

				const officerEmails = normalizeOfficerEmails(lead.OfficerEmails);
				const fallbackRecipient = officerEmails[0] || (lead.Email || '').trim();
				const recipient = (initialEmailRecipients[key] || fallbackRecipient || '').trim();
				if (!recipient) {
					failed += 1;
					setDraftMessages((prev) => ({
						...prev,
						[key]: { success: '', error: 'No recipient selected. Choose an Officer Email.' },
					}));
					continue;
				}

				const draftWithDisclaimer = ensureInitialEmailDisclaimer(draft);

				try {
					const response = await fetch(`${API_BASE}/sales/leads/${lead.id}/initial-email`, {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						credentials: 'include',
						body: JSON.stringify({ initialEmail: draftWithDisclaimer, recipientEmail: recipient }),
					});

					if (!response.ok) {
						throw new Error('Failed to send initial email');
					}

					sent += 1;
					setDraftMessages((prev) => ({
						...prev,
						[key]: { success: 'Initial Email sent.', error: '' },
					}));
					setInitialEmailDrafts((prev) => ({
						...prev,
						[key]: draftWithDisclaimer,
					}));
				} catch (innerErr) {
					console.error(innerErr);
					failed += 1;
					setDraftMessages((prev) => ({
						...prev,
						[key]: { success: '', error: 'Failed to send Initial Email.' },
					}));
				}
			}

			setBulkSendFeedback(`Initial Email send — Sent: ${sent}, Failed: ${failed}.`);
			if (sent > 0) {
				await fetchLeads();
			}
		} catch (err) {
			console.error(err);
			setBulkSendError('Initial Email send failed unexpectedly.');
		} finally {
			setBulkSendRunning(false);
		}
	};

	const handleProgressChange = async (leadId, nextValue) => {
		if (!leadId) {
			return;
		}
		const normalized = PROGRESS_VALUE_SET.has(nextValue) ? nextValue : 'New';
		const key = String(leadId);
		setProgressUpdating((prev) => ({ ...prev, [key]: true }));
		setError('');
		setSuccessMessage('');
		try {
			const response = await fetch(`${API_BASE}/sales/progress`, {
				method: 'PUT',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify({ leadId, progress: normalized }),
			});
			const payload = await response.json().catch(() => null);
			if (!response.ok) {
				const reason = payload?.message || 'Failed to update lead progress.';
				throw new Error(reason);
			}
			setSuccessMessage(payload?.message || 'Lead progress updated.');
			await fetchLeads();
		} catch (err) {
			console.error(err);
			setError(err.message || 'Unable to update lead progress.');
		} finally {
			setProgressUpdating((prev) => {
				const next = { ...prev };
				delete next[key];
				return next;
			});
		}
	};

	const handleCopyTrialUrl = async (leadId) => {
		const trialUrl = `https://insightos.co.uk/trial/${leadId}`;
		try {
			if (navigator?.clipboard?.writeText) {
				await navigator.clipboard.writeText(trialUrl);
			} else {
				const tempInput = document.createElement('textarea');
				tempInput.value = trialUrl;
				tempInput.style.position = 'fixed';
				tempInput.style.left = '-9999px';
				document.body.appendChild(tempInput);
				tempInput.focus();
				tempInput.select();
				document.execCommand('copy');
				document.body.removeChild(tempInput);
			}
			setSuccessMessage('Trial URL copied to clipboard.');
			setError('');
		} catch (copyError) {
			console.error(copyError);
			setError('Unable to copy the trial URL. Please copy it manually.');
		}
	};

	const openMeetingModal = (lead) => {
		const officerEmails = normalizeOfficerEmails(lead.OfficerEmails);
		const fallback = officerEmails[0] || (lead.Email || '').trim();
		setMeetingLead({
			id: lead.id,
			label: getLeadLabel(lead),
			company: lead.Company || lead.CompanyName || lead.BusinessName || '',
			officerEmails,
		});
		setMeetingInvitee(fallback);
		setMeetingValidationError('');
		setMeetingModalOpen(true);
	};

	const closeMeetingModal = () => {
		setMeetingModalOpen(false);
		setMeetingLead(null);
		setMeetingInvitee('');
		setMeetingValidationError('');
	};

	const handleMeetingContinue = () => {
		if (!meetingLead) {
			return;
		}
		const trimmed = (meetingInvitee || '').trim();
		if (!trimmed || !EMAIL_REGEX.test(trimmed)) {
			setMeetingValidationError('Enter a valid recipient email to invite.');
			return;
		}
		navigate('/create-meeting', {
			state: {
				leadId: meetingLead.id,
				leadLabel: meetingLead.label,
				attendeeEmail: trimmed,
				officerEmails: meetingLead.officerEmails,
				company: meetingLead.company,
			},
		});
		closeMeetingModal();
	};

	const formatStatValue = useCallback((value, type = 'number') => {
		const numeric = Number(value ?? 0);
		if (Number.isNaN(numeric)) {
			return '0';
		}
		if (type === 'currency') {
			return new Intl.NumberFormat('en-GB', {
				style: 'currency',
				currency: 'GBP',
				maximumFractionDigits: 0,
			}).format(numeric);
		}
		return numeric.toLocaleString();
	}, []);

	const adminStatEntries = useMemo(() => {
		if (!adminDetails) {
			return [];
		}
		const numberFor = (fields, type = 'number') => {
			const raw = Number(pickField(adminDetails, ...fields));
			return {
				value: Number.isNaN(raw) ? 0 : raw,
				type,
			};
		};
		return [
			{ label: 'Sales', ...numberFor(['Sales', 'sales'], 'currency') },
			{ label: 'MRR', ...numberFor(['MRR', 'mrr'], 'currency') },
			{ label: 'Leads Worked', ...numberFor(['LeadsWorked', 'leadsWorked', 'Leads'], 'number') },
			{
				label: 'Recurring Commission',
				...numberFor(['RecurringCommisson', 'RecurringCommission', 'recurringCommission'], 'currency'),
			},
			{ label: 'Outstanding Bonus', ...numberFor(['OutstandingBonus', 'outstandingBonus'], 'currency') },
			{ label: 'Bonus Commission', ...numberFor(['BonusCommission', 'bonusCommission'], 'currency') },
		];
	}, [adminDetails]);

	const adminInfoColumns = useMemo(
		() => [
			{ key: 'Name', label: 'Name', fields: ['Name', 'name'] },
			{ key: 'TradingName', label: 'Trading Name', fields: ['TradingName', 'tradingName'] },
			{ key: 'Country', label: 'Country', fields: ['Country', 'country'] },
			{ key: 'Telephone', label: 'Telephone', fields: ['Telephone', 'telephone', 'Phone', 'phone'] },
			{ key: 'Description', label: 'Description', fields: ['Description', 'description'] },
			{ key: 'SMTPHost', label: 'SMTP Host', fields: ['SMTPHost'] },
			{ key: 'SMPTPPort', label: 'SMTP Port', fields: ['SMPTPPort'] },
			{ key: 'SMPTUsername', label: 'SMTP Username', fields: ['SMPTUsername'] },
			{ key: 'SMPTSecure', label: 'SMTP Secure', fields: ['SMPTSecure'] },
			{ key: 'Sales', label: 'Sales', fields: ['Sales', 'sales'], type: 'currency' },
			{ key: 'MRR', label: 'MRR', fields: ['MRR', 'mrr'], type: 'currency' },
		],
		[],
	);

	const adminInfoRow = useMemo(() => {
		if (!adminDetails) {
			return null;
		}
		return adminInfoColumns.map((column) => {
			const rawValue = pickField(adminDetails, ...column.fields);
			if (column.key === 'SMPTSecure') {
				return Number(rawValue || 0) === 1 ? 'Yes' : 'No';
			}
			if (column.type === 'currency') {
				return formatStatValue(rawValue, 'currency');
			}
			return rawValue ?? '—';
		});
	}, [adminDetails, adminInfoColumns, formatStatValue]);

	const activeSelectionCount = activeTab === 'available' ? availableSelection.length : ownedSelection.length;
	const visibleLeads = activeTab === 'available' ? availableLeads : filteredOwnedLeads;
	const selectedTemplate = useMemo(() => {
		if (!selectedTemplateId) {
			return null;
		}
		return templates.find((template) => String(template.id) === String(selectedTemplateId)) || null;
	}, [selectedTemplateId, templates]);
	const templatePreview = selectedTemplate?.content?.trim();
	const baseLeadColumnCount = 31;
	const ownedExtraColumns = 4;
	const detailColumnSpan = activeTab === 'owned' ? baseLeadColumnCount + ownedExtraColumns : baseLeadColumnCount;

	const tabClassName = (tab) =>
		[
			'rounded-full px-4 py-2 text-sm font-semibold transition-all duration-200',
			activeTab === tab
				? 'bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 text-slate-900 shadow-lg shadow-purple-500/40'
				: 'bg-white/5 text-slate-200 hover:bg-white/10 hover:text-white',
		].join(' ');

	if (!auth) {
		return (
			<div className="min-h-screen bg-[#05021b] px-4 py-16 text-white">
				<div className="mx-auto max-w-md rounded-3xl border border-white/10 bg-gradient-to-br from-[#1c1a4a] via-[#0b0830] to-[#05021b] p-10 text-center shadow-[0_20px_90px_rgba(12,10,45,0.65)]">
					<p className="text-lg font-semibold tracking-wide text-white">Please sign in</p>
					<p className="mt-3 text-sm text-slate-300">Your sales workspace unlocks after authentication.</p>
				</div>
			</div>
		);
	}

	return (
		<>
			<div className="min-h-screen bg-[#05021b] px-4 py-10 text-white sm:px-6 lg:px-10">
				<div className="mx-auto flex w-full max-w-7xl flex-col gap-8">
				{(adminLoading || adminDetails || adminError) && (
					<section className="rounded-3xl border border-white/10 bg-[#060b1e] p-6 shadow-2xl">
						<div className="flex flex-wrap items-center justify-between gap-4">
							<div>
								<p className="text-xs uppercase tracking-[0.4em] text-sky-300">Staff overview</p>
								<h2 className="mt-2 text-2xl font-semibold text-white">Your sales performance</h2>
							</div>
							<div className="flex items-center gap-2">
								<button
									type="button"
									onClick={checkSmtpConnection}
									className="rounded-full border border-emerald-300/40 px-4 py-2 text-sm text-emerald-100 transition hover:border-emerald-200/70"
									disabled={smtpCheckLoading || !adminDetails}
								>
									{smtpCheckLoading ? 'Checking SMTP...' : 'Check SMTP'}
								</button>
								<button
									type="button"
									onClick={fetchAdminDetails}
									className="rounded-full border border-white/15 px-4 py-2 text-sm text-slate-200 transition hover:border-indigo-200/60"
								>
									Refresh staff data
								</button>
							</div>
						</div>
						{adminError && <p className="mt-4 text-sm text-rose-200">{adminError}</p>}
						{smtpCheckMessage && <p className="mt-2 text-sm text-emerald-200">{smtpCheckMessage}</p>}
						{adminLoading && <p className="mt-4 text-sm text-slate-300">Loading staff details…</p>}
						{adminDetails && (
							<>
								<div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
									{adminStatEntries.map((entry) => (
										<div
											key={entry.label}
											className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#1e1c4d] to-[#09061f] p-4 shadow-lg"
										>
											<p className="text-xs uppercase tracking-[0.3em] text-slate-400">{entry.label}</p>
											<p className="mt-2 text-2xl font-semibold text-white">{formatStatValue(entry.value, entry.type)}</p>
										</div>
									))}
								</div>
								<div className="mt-6 overflow-x-auto rounded-2xl border border-white/10 bg-[#030712]">
									<table className="min-w-full divide-y divide-white/10 text-left text-sm text-slate-100">
										<thead className="text-xs uppercase tracking-[0.2em] text-slate-400">
											<tr>
												{adminInfoColumns.map((column) => (
													<th key={column.key} className="px-4 py-3">
														{column.label}
													</th>
												))}
											</tr>
										</thead>
										<tbody>
											{adminInfoRow ? (
												<tr>
													{adminInfoRow.map((value, index) => (
														<td key={adminInfoColumns[index].key} className="px-4 py-3">
															{value || '—'}
														</td>
													))}
												</tr>
											) : (
												<tr>
													<td colSpan={adminInfoColumns.length} className="px-4 py-3 text-center text-slate-400">
														No staff details available.
													</td>
												</tr>
											)}
										</tbody>
									</table>
								</div>
							</>
						)}
					</section>
				)}
				<section className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#1b1a4b] via-[#0c0a2b] to-[#070312] p-8 shadow-[0_25px_140px_rgba(15,6,45,0.7)]">
					<div className="relative z-10 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
						<div>
							<p className="text-xs uppercase tracking-[0.4em] text-sky-300">Live pipeline</p>
							<h1 className="mt-4 text-3xl font-semibold text-white lg:text-4xl">Sales Lead Command Center</h1>
							<p className="mt-3 max-w-2xl text-sm text-slate-200">
								Claim Companies House qualified leads, narrate conversations, and launch outreach without leaving this canvas.
							</p>
						</div>
						<div className="flex flex-wrap gap-3">
							<button
								type="button"
								onClick={() => setActiveTab('available')}
								className="rounded-full border border-white/20 px-5 py-2 text-sm font-semibold text-white backdrop-blur transition hover:border-indigo-200 hover:text-indigo-100"
							>
								Browse available
							</button>
							<button
								type="button"
								onClick={fetchLeads}
								className="rounded-full bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 px-5 py-2 text-sm font-semibold text-slate-900 shadow-lg shadow-purple-500/40 transition hover:shadow-pink-400/40"
							>
								Refresh data
							</button>
							<Link
								to="/logout"
								className="rounded-full border border-rose-400/60 px-5 py-2 text-sm font-semibold text-rose-100 transition hover:border-rose-200 hover:bg-rose-500/10 hover:text-white"
							>
								Logout
							</Link>
						</div>
					</div>
					<div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(99,102,241,0.3),transparent_65%)]" />
					<img
						src="/2.png"
						alt="Company logo"
						className="pointer-events-none absolute -right-8 top-6 w-40 opacity-80 drop-shadow-[0_10px_35px_rgba(88,28,135,0.6)] lg:w-56"
					/>
				</section>

				<section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
					<StatCard label="Available" value={availableLeads.length} trend="Ready now" />
					<StatCard label="My Leads" value={ownedLeads.length} trend="Owned" />
					<StatCard
						label="Officer Emails"
						value={ownedLeads.filter((lead) => (lead.InitialEmail ?? '').trim().length > 0).length}
						trend="Drafted"
					/>
					<StatCard
						label="Last 7d Touches"
						value={ownedLeads.filter((lead) => {
							if (!lead.LastContacted) return false;
							const contactDate = new Date(lead.LastContacted);
							if (Number.isNaN(contactDate.getTime())) return false;
							const diff = Date.now() - contactDate.getTime();
							return diff <= 7 * 24 * 60 * 60 * 1000;
						}).length}
						trend="Follow-ups"
					/>
				</section>

				{(error || successMessage) && (
					<div className={`rounded-2xl border px-4 py-3 text-sm shadow-lg ${error ? 'border-rose-500/40 bg-rose-500/10 text-rose-100' : 'border-indigo-400/40 bg-indigo-500/10 text-indigo-100'}`}>
						{error || successMessage}
					</div>
				)}

				<section className="rounded-3xl border border-white/10 bg-[#050d1d] p-6 shadow-2xl">
					<div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
						<div className="flex flex-wrap items-center gap-3">
							<button type="button" onClick={() => setActiveTab('available')} className={tabClassName('available')}>
								Available deck
							</button>
							<button type="button" onClick={() => setActiveTab('owned')} className={tabClassName('owned')}>
								My queue
							</button>
							<button
								type="button"
								onClick={fetchLeads}
								className="rounded-full border border-white/10 px-4 py-2 text-sm text-slate-200 transition hover:border-indigo-200/60 hover:text-white"
							>
								Sync now
							</button>
						</div>
						<div className="flex flex-wrap items-center gap-4 text-sm text-slate-300">
							<span>{visibleLeads.length} lead(s)</span>
							<span>{activeSelectionCount} selected</span>
							<button
								type="button"
								onClick={toggleSelectAll}
								className="rounded-full border border-white/15 px-3 py-1 text-xs uppercase tracking-wide text-slate-200 hover:border-indigo-200/60"
							>
								{activeSelectionCount === visibleLeads.length && visibleLeads.length > 0 ? 'Clear selection' : 'Select all'}
							</button>
							{activeTab === 'available' && (
								<button
									type="button"
									onClick={handleClaimSelected}
									disabled={assigning || selectedAvailableLeads.length === 0}
									className="rounded-full bg-gradient-to-r from-indigo-400 via-purple-500 to-pink-400 px-4 py-1 text-xs font-semibold text-slate-900 shadow-lg shadow-purple-600/40 transition hover:shadow-pink-500/50 disabled:cursor-not-allowed disabled:opacity-60"
								>
									Capture selected
								</button>
							)}
							{activeTab === 'owned' && (
								<label className="flex items-center gap-2 rounded-full border border-white/15 px-3 py-1 text-xs uppercase tracking-wide text-slate-200">
									<span>Progress</span>
									<select
										className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-400"
										value={progressFilter}
										onChange={(event) => setProgressFilter(event.target.value)}
									>
										<option value="all">All statuses</option>
										{PROGRESS_OPTIONS.map((option) => (
											<option key={option.value} value={option.value}>
												{option.label}
											</option>
										))}
									</select>
								</label>
							)}
						</div>

						{activeTab === 'owned' && (
							<div className="mt-6 rounded-2xl border border-white/10 bg-[#070f23] p-4">
								<div className="flex flex-wrap items-center justify-between gap-2">
									<p className="text-xs uppercase tracking-[0.3em] text-slate-400">Pipeline progress</p>
									<p className="text-xs text-slate-500">{ownedLeads.length} total lead(s)</p>
								</div>
								<div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
									{PROGRESS_OPTIONS.map((option) => {
										const count = progressStats[option.value] || 0;
										const percentage = ownedLeads.length ? Math.round((count / ownedLeads.length) * 100) : 0;
										return (
											<div key={option.value} className="rounded-2xl border border-white/10 bg-white/5 p-3">
												<div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-slate-400">
													<span>{option.label}</span>
													<span>{percentage}%</span>
												</div>
												<p className="mt-1 text-2xl font-semibold text-white">{count}</p>
												<div className="mt-2 h-1.5 w-full rounded-full bg-white/10">
													<div className={`h-full rounded-full ${option.barClass}`} style={{ width: `${Math.min(percentage, 100)}%` }} />
												</div>
											</div>
										);
									})}
								</div>
							</div>
						)}
					</div>

					<div className="mt-4 overflow-x-auto rounded-2xl border border-white/5 bg-gradient-to-b from-[#070f23] to-[#050c1b]">
						<table className="min-w-full divide-y divide-white/5 text-left text-sm text-slate-100">
							<thead className="text-xs uppercase tracking-[0.2em] text-slate-400">
								<tr>
									<th className="px-4 py-3">Select</th>
									<th className="px-4 py-3">Progress</th>
									<th className="px-4 py-3">Lead</th>
									<th className="px-4 py-3">Company</th>
									<th className="px-4 py-3">Occupation</th>
									<th className="px-4 py-3">Location</th>
									<th className="px-4 py-3">Address</th>
									<th className="px-4 py-3">Email</th>
									<th className="px-4 py-3">Telephone</th>
									<th className="px-4 py-3">Website</th>
									<th className="px-4 py-3">Officer Emails</th>
									<th className="px-4 py-3">Companies House</th>
									<th className="px-4 py-3">Contacted</th>
									<th className="px-4 py-3">Initial Email</th>
									<th className="px-4 py-3">Responded</th>
									<th className="px-4 py-3">Email Template</th>
									<th className="px-4 py-3">Created</th>
									<th className="px-4 py-3">Staff</th>
									<th className="px-4 py-3">Sector</th>
									<th className="px-4 py-3">Filter</th>
									<th className="px-4 py-3">Pursue</th>
									<th className="px-4 py-3">Company Size</th>
									<th className="px-4 py-3">Pain Point Report</th>
									<th className="px-4 py-3">URL</th>
									<th className="px-4 py-3">Sales Report</th>
									<th className="px-4 py-3">Email Campaign</th>
									<th className="px-4 py-3">Live</th>
									<th className="px-4 py-3">Demo</th>
									<th className="px-4 py-3">Meetings</th>
									<th className="px-4 py-3">Google Meet</th>
									<th className="px-4 py-3">Details</th>
									{activeTab === 'owned' && (
										<>
											<th className="px-4 py-3">Conversation</th>
											<th className="px-4 py-3">Last Contact</th>
											<th className="px-4 py-3">Next Contact</th>
											<th className="px-4 py-3">Save</th>
										</>
									)}
								</tr>
							</thead>
							<tbody className="divide-y divide-white/5">
								{visibleLeads.map((lead) => {
									const selected = activeTab === 'available' ? availableSelection.includes(lead.id) : ownedSelection.includes(lead.id);
									const notes = getNotesDraft(lead);
									const isExpanded = expandedLeadIds.includes(lead.id);
									const leadKey = String(lead.id);
									const officerEmailList = (lead.OfficerEmails || '')
										.split(/[;,]/)
										.map((entry) => entry.trim())
										.filter(Boolean);
									const progressValue = PROGRESS_VALUE_SET.has(lead.Progress) ? lead.Progress : 'New';
									const progressMeta = getProgressMeta(progressValue);
									const progressBusy = Boolean(progressUpdating[leadKey]);
									const respondedDisplay =
										lead.Responded === null || lead.Responded === undefined
											? '—'
											: Number(lead.Responded) === 1
												? 'Yes'
												: Number(lead.Responded) === 0
													? 'No'
													: safeText(lead.Responded);
									const websiteLink = lead.Website ? (lead.Website.startsWith('http') ? lead.Website : `https://${lead.Website}`) : null;
									const urlLink = lead.URL ? (lead.URL.startsWith('http') ? lead.URL : `https://${lead.URL}`) : null;
									const staffDisplay = [lead.StaffName || lead.SalesmanName || 'Unassigned', lead.StaffId ? `#${lead.StaffId}` : null]
										.filter(Boolean)
										.join(' ');
									const filterDisplay =
										lead.Filter === null || lead.Filter === undefined
											? '—'
											: Number(lead.Filter) === 1
												? 'Yes'
												: Number(lead.Filter) === 0
													? 'No'
													: safeText(lead.Filter);
									const liveDisplay =
										lead.Live === null || lead.Live === undefined
											? '—'
											: Number(lead.Live) === 1
												? 'Live'
												: Number(lead.Live) === 0
													? 'Off'
													: safeText(lead.Live);
									const initialEmailSnippet = summarizeText(lead.InitialEmail);
									const painPointSnippet = summarizeText(lead.PainPointReport, 50);
									return (
										<React.Fragment key={lead.id}>
											<tr className={selected ? 'bg-white/5' : ''}>
												<td className="px-4 py-3">
													<input
														type="checkbox"
														className="h-4 w-4 rounded border-white/30 bg-transparent text-indigo-400 focus:ring-indigo-400"
														checked={selected}
														onChange={() => toggleSelection(lead.id)}
													/>
												</td>
												<td className="px-4 py-3">
													<div className="space-y-2">
														<p className={`text-xs font-semibold ${progressMeta.textClass}`}>{progressMeta.label}</p>
														<div className="h-1.5 w-full rounded-full bg-white/10">
															<div className={`h-full rounded-full ${progressMeta.barClass}`} />
														</div>
														{activeTab === 'owned' && (
															<select
																className="w-full rounded-xl border border-white/10 bg-white px-3 py-1 text-xs text-slate-900 focus:border-indigo-400 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
																value={progressValue}
																onChange={(event) => handleProgressChange(lead.id, event.target.value)}
																disabled={progressBusy}
															>
																{PROGRESS_OPTIONS.map((option) => (
																	<option key={option.value} value={option.value}>
																		{option.label}
																	</option>
																))}
															</select>
														)}
														{progressValue === 'Interested' && activeTab === 'owned' && (
															<div className="space-y-1">
																<button
																	type="button"
																	onClick={() => openMeetingModal(lead)}
																	className="w-full rounded-full bg-gradient-to-r from-emerald-400 to-cyan-400 px-3 py-1 text-xs font-semibold text-slate-900 shadow-lg shadow-emerald-500/40 transition hover:shadow-cyan-400/40"
																>
																	Book Meeting
																</button>
																<button
																	type="button"
																	onClick={() => handleCopyTrialUrl(lead.id)}
																	className="w-full rounded-full border border-white/20 px-3 py-1 text-xs font-semibold text-white transition hover:border-indigo-200/60"
																>
																	Copy Trial URL
																</button>
															</div>
														)}
													</div>
												</td>
												<td className="px-4 py-3">
													<p className="font-semibold text-white">{lead.LeadName || getLeadLabel(lead)}</p>
													<p className="text-xs text-slate-400">{safeText(lead.Occupation)}</p>
												</td>
												<td className="px-4 py-3 text-slate-200">{safeText(lead.Company || lead.CompanyName || lead.BusinessName)}</td>
												<td className="px-4 py-3 text-slate-200">{safeText(lead.Occupation)}</td>
												<td className="px-4 py-3 text-slate-200">{safeText(lead.Location)}</td>
												<td className="px-4 py-3 text-slate-200">{safeText(lead.Address)}</td>
												<td className="px-4 py-3 text-slate-200">
													{lead.Email ? (
														<a href={`mailto:${lead.Email}`} className="text-indigo-200 underline-offset-2 hover:underline">
															{lead.Email}
														</a>
													) : (
														<span className="text-slate-500">—</span>
													)}
												</td>
												<td className="px-4 py-3 text-slate-200">{safeText(lead.Telephone)}</td>
												<td className="px-4 py-3">
													{websiteLink ? (
														<a href={websiteLink} className="text-indigo-200 underline-offset-2 hover:underline" target="_blank" rel="noreferrer">
															Visit
														</a>
													) : (
														<span className="text-slate-500">—</span>
													)}
												</td>
												<td className="px-4 py-3">
													{officerEmailList.length ? (
														<span className="rounded-full bg-indigo-500/15 px-3 py-1 text-xs text-indigo-200">
															{officerEmailList.length} email(s)
														</span>
													) : (
														<span className="text-slate-500">None</span>
													)}
												</td>
												<td className="px-4 py-3">
													{Number(lead.CompaniesHouseHit ?? 0) === 1 ? (
														<span className="rounded-full bg-violet-400/15 px-3 py-1 text-xs text-violet-100">Match</span>
													) : (
														<span className="text-slate-500">—</span>
													)}
												</td>
												<td className="px-4 py-3 text-slate-200">{formatDateTime(lead.Contacted)}</td>
												<td className="px-4 py-3 text-slate-200">{initialEmailSnippet || '—'}</td>
												<td className="px-4 py-3 text-slate-200">{respondedDisplay}</td>
												<td className="px-4 py-3 text-slate-200">{safeText(lead.EmailTemplate)}</td>
												<td className="px-4 py-3 text-slate-200">{formatDateTime(lead.CreatedAt)}</td>
												<td className="px-4 py-3 text-slate-200">{staffDisplay || '—'}</td>
												<td className="px-4 py-3 text-slate-200">{safeText(lead.Sector)}</td>
												<td className="px-4 py-3 text-slate-200">{filterDisplay}</td>
												<td className="px-4 py-3 text-slate-200">{safeText(lead.Pursue)}</td>
												<td className="px-4 py-3 text-slate-200">{safeText(lead.CompanySize)}</td>
												<td className="px-4 py-3 text-slate-200">{painPointSnippet || '—'}</td>
												<td className="px-4 py-3">
													{urlLink ? (
														<a href={urlLink} className="text-indigo-200 underline-offset-2 hover:underline" target="_blank" rel="noreferrer">
															Open
														</a>
													) : (
														<span className="text-slate-500">—</span>
													)}
												</td>
												<td className="px-4 py-3 text-slate-200">{safeText(lead.SalesReport)}</td>
												<td className="px-4 py-3 text-slate-200">{safeText(lead.EmailCampaign)}</td>
												<td className="px-4 py-3 text-slate-200">{liveDisplay}</td>
												<td className="px-4 py-3 text-slate-200">{safeText(lead.Demo)}</td>
												<td className="px-4 py-3 text-slate-200">{safeText(lead.Meetings)}</td>
												<td className="px-4 py-3">
													{lead.GoogleMeetLink ? (
														<a href={lead.GoogleMeetLink} className="text-indigo-200 underline-offset-2 hover:underline" target="_blank" rel="noreferrer">
															Meet
														</a>
													) : (
														<span className="text-slate-500">—</span>
													)}
												</td>
												<td className="px-4 py-3">
													<button
														type="button"
														onClick={() => toggleLeadDetails(lead.id)}
														className="rounded-full border border-white/15 px-3 py-1 text-xs uppercase tracking-wide text-slate-200 transition hover:border-indigo-200/60"
													>
														{isExpanded ? 'Hide' : 'Details'}
													</button>
												</td>
												{activeTab === 'owned' && (
													<>
														<td className="px-4 py-3">
															<textarea
																className="w-full rounded-xl border border-white/10 bg-white/5 p-3 text-xs text-white focus:border-indigo-400 focus:outline-none"
																rows={3}
																value={notes.Conversation}
																onChange={(event) => handleNotesChange(lead.id, 'Conversation', event.target.value)}
															/>
														</td>
														<td className="px-4 py-3">
															<input
																type="date"
																className="w-full rounded-xl border border-white/10 bg-white/5 p-2 text-white focus:border-indigo-400 focus:outline-none"
																value={notes.LastContacted}
																onChange={(event) => handleNotesChange(lead.id, 'LastContacted', event.target.value)}
															/>
															<p className="mt-1 text-xs text-slate-500">{formatDisplayDate(lead.LastContacted)}</p>
														</td>
														<td className="px-4 py-3">
															<input
																type="date"
																className="w-full rounded-xl border border-white/10 bg-white/5 p-2 text-white focus:border-indigo-400 focus:outline-none"
																value={notes.NextContact}
																onChange={(event) => handleNotesChange(lead.id, 'NextContact', event.target.value)}
															/>
															<p className="mt-1 text-xs text-slate-500">{formatDisplayDate(lead.NextContact)}</p>
														</td>
														<td className="px-4 py-3">
															<button
																type="button"
																onClick={() => handleSaveNotes(lead.id)}
																disabled={notesSavingId === lead.id}
																className="rounded-full bg-gradient-to-r from-indigo-400 via-purple-500 to-pink-400 px-4 py-1 text-xs font-semibold text-slate-900 shadow-lg shadow-purple-600/40 transition hover:shadow-pink-500/50 disabled:cursor-not-allowed disabled:opacity-60"
															>
																Save
															</button>
														</td>
													</>
												)}
											</tr>
											{isExpanded && (
												<tr className="bg-white/5">
													<td colSpan={detailColumnSpan} className="px-4 py-4">
														<div className="space-y-4">
															<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
																<DetailField label="Initial email" value={lead.InitialEmail}
																/>
																<DetailField label="Notes" value={lead.Notes} />
																<DetailField label="Conversation" value={lead.Conversation} />
																<DetailField label="Officer emails" value={lead.OfficerEmails} />
																<DetailField label="Staff ID" value={lead.StaffId} />
																<DetailField label="Staff name" value={lead.StaffName || lead.SalesmanName} />
																<DetailField label="Company" value={lead.Company} />
																<DetailField label="Address" value={lead.Address} />
																<DetailField label="Telephone" value={lead.Telephone} />
																<DetailField label="Email" value={lead.Email} />
																<DetailField label="URL" value={lead.URL} />
																<DetailField label="Website" value={lead.Website} />
																<DetailField label="Sales report" value={lead.SalesReport} />
																<DetailField label="Email campaign" value={lead.EmailCampaign} />
																<DetailField label="Pursue" value={lead.Pursue} />
																<DetailField label="Company size" value={lead.CompanySize} />
																<DetailField label="Demo" value={lead.Demo} />
																<DetailField label="Meetings" value={lead.Meetings} />
																<DetailField label="Google Meet link" value={lead.GoogleMeetLink} />
															</div>
															<div className="grid gap-4 md:grid-cols-3">
																<CollapsibleText label="Company report" value={lead.CompanyReport} />
																<CollapsibleText label="Pain point report" value={lead.PainPointReport} />
																<CollapsibleText label="Job listings" value={lead.JobLisitings} />
															</div>
														</div>
													</td>
												</tr>
											)}
										</React.Fragment>
									);
								})}
								{visibleLeads.length === 0 && (
									<tr>
										<td colSpan={detailColumnSpan} className="px-4 py-6 text-center text-slate-500">
											{loading ? 'Loading leads…' : 'No leads found for this tab.'}
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>

					{activeTab === 'owned' && (
						<div className="mt-8 space-y-4 rounded-3xl border border-white/10 bg-[#040b16] p-5">
							<div className="flex flex-wrap items-center gap-4">
								<div className="flex flex-col text-sm">
									<span className="text-xs uppercase tracking-[0.3em] text-slate-500">Initial email · apply AI template</span>
									<select
										className="rounded-2xl border border-white/10 bg-white px-4 py-2 text-sm font-medium text-slate-900 focus:border-indigo-400 focus:outline-none"
										value={selectedTemplateId}
										onChange={(event) => setSelectedTemplateId(event.target.value)}
									>
										<option value="">Select AI template</option>
										{templates.map((template) => (
											<option key={template.id} value={template.id}>
												{describeTemplateOption(template)}
											</option>
										))}
									</select>
									<p className="mt-2 text-xs text-slate-400">Applying a template overwrites the selected leads' Initial Email content.</p>
									{templatePreview && (
										<div className="mt-3 rounded-2xl border border-white/10 bg-[#030811] p-3 text-xs text-slate-200">
											<p className="text-[0.65rem] uppercase tracking-[0.4em] text-slate-500">AI template content</p>
											<p className="mt-2 whitespace-pre-line text-sm text-slate-100">{templatePreview}</p>
										</div>
									)}
								</div>
								<button
									type="button"
									onClick={handleBulkApplyTemplate}
									disabled={bulkCampaignRunning || selectedOwnedLeads.length === 0}
									className="rounded-full bg-gradient-to-r from-indigo-400 via-violet-500 to-fuchsia-500 px-5 py-2 text-sm font-semibold text-slate-900 shadow-lg shadow-violet-500/40 transition hover:shadow-fuchsia-400/50 disabled:cursor-not-allowed disabled:opacity-60"
								>
									Apply AI template
								</button>
								<button
									type="button"
									onClick={() => setDraftPanelOpen((prev) => !prev)}
									className="rounded-full border border-white/15 px-5 py-2 text-sm text-slate-200 transition hover:border-indigo-200/60"
								>
									{draftPanelOpen ? 'Hide Initial Email editor' : 'Edit Initial Emails'}
								</button>
								<button
									type="button"
									onClick={handleBulkSendEmails}
									disabled={bulkSendRunning || selectedOwnedLeads.length === 0}
									className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-400 via-indigo-500 to-purple-500 px-5 py-2 text-sm font-semibold text-slate-900 shadow-lg shadow-indigo-500/40 transition hover:shadow-purple-400/40 disabled:cursor-not-allowed disabled:opacity-60"
								>
									<PaperAirplaneIcon className="h-4 w-4" />
									Send Initial Emails
								</button>
							</div>
							<div className="flex flex-wrap items-center gap-4 text-xs text-slate-400">
								{templateError && (
									<div className="inline-flex items-center gap-3 rounded-full border border-rose-400/40 bg-rose-500/10 px-3 py-1 text-rose-200">
										<span>{templateError}</span>
										<button type="button" onClick={handleTemplateRetry} className="font-semibold text-white underline-offset-2 hover:underline">
											Retry
										</button>
									</div>
								)}
								{(bulkCampaignError || bulkCampaignFeedback) && (
									<p className={bulkCampaignError ? 'text-rose-300' : 'text-indigo-200'}>
										{bulkCampaignError || bulkCampaignFeedback}
									</p>
								)}
								{(bulkSendError || bulkSendFeedback) && (
									<p className={bulkSendError ? 'text-rose-300' : 'text-indigo-200'}>
										{bulkSendError || bulkSendFeedback}
									</p>
								)}
								{templatesLoading && <p className="text-slate-300">Loading AI templates…</p>}
								<p className="text-slate-400">{`Every Initial Email automatically adds "${UNSUBSCRIBE_DISCLAIMER}" to the end of the message.`}</p>
							</div>

							{draftPanelOpen && (
								<div className="mt-4 space-y-4 rounded-2xl border border-white/10 bg-[#050c1b] p-4">
									{selectedOwnedLeads.length === 0 && (
										<p className="text-sm text-slate-400">Select at least one lead to edit Initial Emails.</p>
									)}
									{selectedOwnedLeads.map((lead) => {
										const key = String(lead.id);
										const draftValue = initialEmailDrafts[key] ?? lead.InitialEmail ?? '';
										const message = draftMessages[key];
										const officerEmails = normalizeOfficerEmails(lead.OfficerEmails);
										const selectedRecipient = initialEmailRecipients[key] || officerEmails[0] || (lead.Email || '').trim() || '';
										return (
											<div key={key} className="space-y-2 rounded-2xl border border-white/5 bg-white/5 p-4">
												<div className="flex flex-wrap items-center justify-between gap-2 text-sm">
													<div>
														<p className="font-semibold text-white">{getLeadLabel(lead)}</p>
														<p className="text-xs text-slate-400">{lead.OfficerEmails}</p>
													</div>
													{message && (
														<p className={`text-xs ${message.error ? 'text-rose-300' : 'text-indigo-200'}`}>
															{message.error || message.success}
														</p>
													)}
												</div>
												<textarea
													className="w-full rounded-2xl border border-white/10 bg-[#030811] p-3 text-sm text-white focus:border-indigo-400 focus:outline-none"
													rows={6}
													value={draftValue}
													onChange={(event) => handleDraftChange(lead.id, event.target.value)}
												/>
												<div className="flex flex-wrap items-center gap-3 text-xs text-slate-300">
													{officerEmails.length > 1 ? (
														<>
															<span className="uppercase tracking-[0.2em] text-slate-400">Officer recipient</span>
															<select
																className="rounded-xl border border-white/10 bg-[#020611] px-3 py-1 text-white focus:border-indigo-400 focus:outline-none"
																value={selectedRecipient}
																onChange={(event) => handleRecipientChange(lead.id, event.target.value)}
															>
																{officerEmails.map((email) => (
																	<option key={email} value={email}>
																		{email}
																	</option>
																))}
															</select>
														</>
													) : (
														<span>
															Sending to {selectedRecipient || 'no recipient available — please add an officer email.'}
														</span>
													)}
												</div>
											</div>
										);
									})}
								</div>
							)}
						</div>
					)}
				</section>
			</div>
		</div>
		{meetingModalOpen && meetingLead && (
			<div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
				<div className="absolute inset-0 bg-black/70 backdrop-blur" onClick={closeMeetingModal} />
				<div className="relative z-10 w-full max-w-lg rounded-3xl border border-white/10 bg-[#050c1b] p-6 shadow-[0_25px_120px_rgba(5,3,20,0.85)]">
					<div className="flex items-start justify-between gap-4">
						<div>
							<p className="text-xs uppercase tracking-[0.4em] text-slate-400">Book demo</p>
							<h3 className="mt-1 text-xl font-semibold text-white">{meetingLead.label}</h3>
							<p className="text-sm text-slate-400">Invite a director to the Google Meet demo.</p>
						</div>
						<button type="button" onClick={closeMeetingModal} className="text-sm text-slate-400 hover:text-white">
							Close
						</button>
					</div>
					<div className="mt-4 space-y-4">
						{meetingLead.officerEmails.length > 0 && (
							<label className="block text-sm text-slate-300">
								<span className="text-xs uppercase tracking-[0.3em] text-slate-500">Officer emails</span>
								<select
									className="mt-1 w-full rounded-xl border border-white/10 bg-white px-3 py-2 text-sm text-slate-900 focus:border-indigo-400 focus:outline-none"
									value={meetingInvitee}
									onChange={(event) => {
										setMeetingInvitee(event.target.value);
										setMeetingValidationError('');
									}}
								>
									{meetingLead.officerEmails.map((email) => (
										<option key={email} value={email}>
											{email}
										</option>
									))}
								</select>
							</label>
						)}
						<label className="block text-sm text-slate-300">
							<span className="text-xs uppercase tracking-[0.3em] text-slate-500">Invitee email</span>
							<input
								type="email"
								className="mt-1 w-full rounded-xl border border-white/10 bg-white/90 px-3 py-2 text-sm text-slate-900 focus:border-indigo-400 focus:outline-none"
								value={meetingInvitee}
								onChange={(event) => {
									setMeetingInvitee(event.target.value);
									setMeetingValidationError('');
								}}
								placeholder="founder@company.com"
							/>
						</label>
						{meetingValidationError && <p className="text-xs text-rose-300">{meetingValidationError}</p>}
					</div>
					<div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
						<button
							type="button"
							onClick={closeMeetingModal}
							className="rounded-full border border-white/20 px-4 py-2 text-sm text-slate-200 hover:border-indigo-200/60"
						>
							Cancel
						</button>
						<button
							type="button"
							onClick={handleMeetingContinue}
							className="rounded-full bg-gradient-to-r from-indigo-400 via-purple-500 to-pink-400 px-4 py-2 text-sm font-semibold text-slate-900 shadow-lg shadow-indigo-500/40"
						>
							Continue to meeting
						</button>
					</div>
				</div>
			</div>
		)}
		</>
	);
}

function StatCard({ label, value, trend }) {
	return (
		<div className="group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#211c61] to-[#090621] p-5 shadow-xl transition-transform duration-300 hover:-translate-y-1">
			<div className="text-xs uppercase tracking-[0.3em] text-slate-400">{label}</div>
			<div className="mt-2 flex items-end justify-between">
				<p className="text-3xl font-semibold text-white">{value}</p>
				<span className="text-xs text-indigo-200">{trend}</span>
			</div>
			<div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-70" style={{ background: 'radial-gradient(circle at top, rgba(99,102,241,0.35), transparent 65%)' }} />
		</div>
	);
}

function CollapsibleText({ label, value }) {
	const [open, setOpen] = useState(false);
	if (!value) {
		return null;
	}
	return (
		<div className="rounded-2xl border border-white/10 bg-white/5 p-4">
			<button
				type="button"
				onClick={() => setOpen((prev) => !prev)}
				className="flex w-full items-center justify-between text-left text-sm font-semibold text-white"
			>
				<span>{label}</span>
				<span className="text-xs text-indigo-200">{open ? 'Hide' : 'Show'}</span>
			</button>
			{open && <p className="mt-3 text-sm text-slate-200 whitespace-pre-line">{value}</p>}
		</div>
	);
}

function DetailField({ label, value }) {
	return (
		<div className="rounded-2xl border border-white/10 bg-[#030811] p-3">
			<p className="text-[0.6rem] uppercase tracking-[0.4em] text-slate-500">{label}</p>
			<p className="mt-2 text-sm text-slate-100 whitespace-pre-line">{safeText(value)}</p>
		</div>
	);
}
